import React, { useContext, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { GridColumn, Grid } from '@progress/kendo-react-grid';
import { withState } from '../../../../components/common/grid/WrappedGrid';
import { ROLES } from '~/src/utils/constants';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import { API_URL, DATE_FORMAT_WITH_TIME } from '../../../../utils/constants';
import { CreatedOnCell } from '../../../../components/common/grid/Cells';
import { grantedForRole, transformLabels } from '../../../../utils/misc';
import PullFnbOrders from '../components/PullFnbOrders';
import SyncFnbOrder from '../components/SyncFnbOrder';
import moment from 'moment';
import ConsolidateFnbOrders from '../components/ConsolidateFnbOrders';
import { FnbOrderGetWithItemsDto } from 'svila-common';
import ResetFnbOrder from '../components/ResetFnbOrder';
import RolesDependent from '~/src/components/common/RolesDependent';
import MyContext from '~/src/services/context';
import GridWrapper from '~/src/components/common/GridWrapper';
import CompleteFnbOrder from '../components/CompleteFnbOrder';

interface FnbRow {
  dataItem: FnbOrderGetWithItemsDto;
}

export default function FnbOrdersList(): JSX.Element {
  const stateRef = useRef(null);
  const url = `${API_URL}/fnb/get`;
  const history = useHistory();
  const context = useContext(MyContext);
  const roles = [ROLES.ADMIN, ROLES.SUPER_ADMIN];
  const permissionGranted = grantedForRole(roles, context.user?.role);
  const StatefullGrid = withState(Grid, url);

  const numberCell = (row) => {
    const { dataItem } = row;

    return (
      <td>
        <Link to={`/orders/fnb/${dataItem.uniqueNumber}`}>{dataItem.uniqueNumber}</Link>
        <RolesDependent roles={[ROLES.SUPER_ADMIN]}>
          <ResetFnbOrder dataItemId={dataItem.id} />
        </RolesDependent>
      </td>
    );
  };

  const getLastSync = (dataItem) => {
    if (dataItem?.items?.length > 0) {
      return dataItem?.modifiedOn
        ? moment(dataItem.modifiedOn).format(DATE_FORMAT_WITH_TIME)
        : moment(dataItem.createdOn).format(DATE_FORMAT_WITH_TIME);
    } else {
      return '--';
    }
  };

  const orderNumberCell = (row) => {
    const { dataItem } = row;

    return (
      <td>
        <Link to={`/orders/details/${dataItem?.order?.id}`} target="_blank">
          {dataItem?.order?.orderNumber}
        </Link>
      </td>
    );
  };

  const consolidationOrderCell = (row) => {
    const { dataItem } = row;
    return (
      <td>
        <Link
          to={`/orders/details/${dataItem?.order?.consolidationParentOrder?.id}/${dataItem?.consolidated}`}
          target="_blank"
        >
          {dataItem?.order?.consolidationParentOrder?.orderNumber}
        </Link>
      </td>
    );
  };

  const priceCell = (row) => {
    const { dataItem } = row;

    return <td>{`${dataItem.total} лв.`}</td>;
  };

  const statusCell = (row) => {
    const { dataItem } = row;

    return <td>{transformLabels(dataItem.statusTxt)}</td>;
  };

  const actionCell = (row: FnbRow) => {
    const { dataItem } = row;

    return (
      <td>
        <SyncFnbOrder onUpdate={updateTable} dataItemId={dataItem.id} />
        {dataItem.status === 3 ? (
          <CompleteFnbOrder onUpdate={updateTable} dataItemId={dataItem.uniqueNumber} />
        ) : null}
        {dataItem.status === 12 ? <div>Completed</div> : null}
        {dataItem.consolidated ? <div>Consolidated</div> : null}
      </td>
    );
  };

  const syncCell = (row) => {
    const { dataItem } = row;
    const lastSync = getLastSync(dataItem);
    return <td>{lastSync}</td>;
  };

  const reservationCell = (row): JSX.Element => {
    const { dataItem }: { dataItem: FnbOrderGetWithItemsDto } = row;

    return <td>{dataItem.fnbItemReservationStatus}</td>;
  };

  const updateTable = () => {
    stateRef?.current?.update();
  };

  return (
    <WhitePad isFluid>
      <TitleComponent
        onLeftHandler={(): void => {
          history.push(`/home`);
        }}
        title="FNB"
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <PullFnbOrders onUpdate={updateTable} />
        <ConsolidateFnbOrders onUpdate={updateTable} />
      </div>
      <GridWrapper>
        <StatefullGrid ref={stateRef}>
          <GridColumn field="statusTxt" title="Gomba Status" cell={statusCell} />
          <GridColumn field="orderDate" title="Дата" cell={CreatedOnCell} filterable={false} />
          <GridColumn field="uniqueNumber" title="FNB #"  cell={numberCell} />
          <GridColumn field="order.orderNumber" title="Svila #" cell={orderNumberCell}  />
          {permissionGranted && (
            <GridColumn field="order.consolidationParentOrder.orderNumber" title="Consolidation #" cell={consolidationOrderCell} />
          )}
          <GridColumn field="name" title="Клиент" />
          <GridColumn field="nameLast" title="Клиент" />
          <GridColumn field="paymentMethodTxt" title="Метод на плащане" />
          <GridColumn field="shCityName" title="Град за доставка" />
          <GridColumn field="total" title="Сума" cell={priceCell} filterable={false} />
          <GridColumn title="Actions" cell={actionCell} width={200} filterable={false} />
          <GridColumn field="total" title="Synced" cell={syncCell} />
          <GridColumn field="fnbItemReservationStatus" title="Reservation" cell={reservationCell} />
          <GridColumn field="problem" title="Problem" />
        </StatefullGrid>
      </GridWrapper>
    </WhitePad>
  );
}
